import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [sectionActive, setSectionActive] = useState("accueil");

  const location = useLocation();
  const navigate = useNavigate();

  const surAccueil = location.pathname === "/";
  const surDemandeProjet = location.pathname === "/demande-projet";
  const surContact = location.pathname === "/contact";

  const liens = [
    { label: "Accueil", id: "accueil" },
    { label: "Services", id: "services" },
    { label: "Équipe", id: "equipe" },
  ];

  // Détecter automatiquement la section visible
  useEffect(() => {
    if (!surAccueil) return;

    const handleScroll = () => {
      const sections = ["accueil", "services", "equipe"];

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);

        if (section) {
          const position = section.getBoundingClientRect();

          if (position.top <= 120) {
            setSectionActive(sections[i]);
            break;
          }
        }
      }
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [surAccueil]);

  // Gestion des liens Accueil / Services / Équipe
  const handleLienClick = (id, e) => {
    e.preventDefault();

    setMenuOpen(false);
    setSectionActive(id);

    // Si on est sur une autre page
    if (!surAccueil) {
      navigate("/");

      setTimeout(() => {
        if (id === "accueil") {
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        } else {
          const element = document.getElementById(id);

          if (element) {
            element.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }
      }, 500);

      return;
    }

    // Si on est déjà sur l'accueil
    if (id === "accueil") {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      const element = document.getElementById(id);

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  };

  return (
    <nav
      className="fixed w-full top-0 z-50 px-8 py-4 flex items-center justify-between"
      style={{
        background: "rgba(10,10,30,0.85)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      {/* LOGO */}
      <Link
        to="/"
        style={{
          cursor: "pointer",
          textDecoration: "none",
        }}
      >
        <span className="text-xl font-extrabold tracking-wide">
          <span style={{ color: "#F5C500" }}>⚡</span>
          <span style={{ color: "#6B21E8" }}>EFE</span>
          <span style={{ color: "white" }}> Systems</span>
        </span>
      </Link>

      {/* MENU DESKTOP */}
      <ul
        className="hidden md:flex items-center gap-2 text-sm font-medium"
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
        }}
      >
        {liens.map((lien) => {
          const estActif =
            surAccueil && sectionActive === lien.id;

          return (
            <li key={lien.id}>
              <a
                href={`#${lien.id}`}
                onClick={(e) => handleLienClick(lien.id, e)}
                style={{
                  background: estActif
                    ? "#1A1AE6"
                    : "transparent",
                  color: estActif
                    ? "white"
                    : "rgba(255,255,255,0.7)",
                  fontWeight: estActif ? "600" : "400",
                  cursor: "pointer",
                  textDecoration: "none",
                  display: "inline-block",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  transition: "all .2s",
                }}
              >
                {lien.label}
              </a>
            </li>
          );
        })}

        {/* CONTACT */}
        <li>
          <Link
            to="/contact"
            style={{
              background: surContact
                ? "#1A1AE6"
                : "transparent",
              color: surContact
                ? "white"
                : "rgba(255,255,255,0.7)",
              fontWeight: surContact ? "600" : "400",
              cursor: "pointer",
              textDecoration: "none",
              display: "inline-block",
              padding: "8px 16px",
              borderRadius: "8px",
              transition: "all .2s",
            }}
          >
            Contact
          </Link>
        </li>
      </ul>

      {/* BOUTONS DESKTOP */}
      <div className="hidden md:flex gap-3 items-center">
        <Link
          to="/login"
          style={{
            color: "rgba(255,255,255,0.7)",
            border: "1px solid rgba(255,255,255,0.2)",
            padding: "8px 16px",
            borderRadius: "8px",
            textDecoration: "none",
            fontSize: "14px",
          }}
        >
          Espace Client
        </Link>

        {!surDemandeProjet && !surContact && (
          <Link
            to="/demande-projet"
            style={{
              background: "#1A1AE6",
              color: "white",
              padding: "8px 20px",
              borderRadius: "8px",
              fontWeight: "600",
              textDecoration: "none",
            }}
          >
            Obtenir un devis →
          </Link>
        )}
      </div>

      {/* HAMBURGER MOBILE */}
      <button
        className="md:hidden"
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label={
          menuOpen
            ? "Fermer le menu"
            : "Ouvrir le menu"
        }
        style={{
          background: "none",
          border: "none",
          color: "white",
          cursor: "pointer",
        }}
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {menuOpen ? (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          ) : (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          )}
        </svg>
      </button>

      {/* MENU MOBILE */}
      {menuOpen && (
        <div
          className="absolute top-16 left-0 w-full px-6 py-4 flex flex-col gap-3 md:hidden"
          style={{
            background: "rgba(10,10,30,0.98)",
          }}
        >
          {liens.map((lien) => {
            const estActif =
              surAccueil && sectionActive === lien.id;

            return (
              <a
                key={lien.id}
                href={`#${lien.id}`}
                onClick={(e) =>
                  handleLienClick(lien.id, e)
                }
                style={{
                  color: estActif
                    ? "#F5C500"
                    : "white",
                  fontWeight: estActif ? "600" : "400",
                  textDecoration: "none",
                  padding: "8px 0",
                }}
              >
                {lien.label}
              </a>
            );
          })}

          {/* CONTACT MOBILE */}
          <Link
            to="/contact"
            onClick={() => setMenuOpen(false)}
            style={{
              color: surContact
                ? "#F5C500"
                : "white",
              fontWeight: surContact ? "600" : "400",
              textDecoration: "none",
              padding: "8px 0",
            }}
          >
            Contact
          </Link>

          {/* ESPACE CLIENT MOBILE */}
          <Link
            to="/login"
            onClick={() => setMenuOpen(false)}
            style={{
              color: "white",
              border: "1px solid rgba(255,255,255,0.2)",
              padding: "8px 16px",
              borderRadius: "8px",
              textAlign: "center",
              textDecoration: "none",
            }}
          >
            Espace Client
          </Link>

          {/* DEVIS MOBILE */}
          {!surDemandeProjet && !surContact && (
            <Link
              to="/demande-projet"
              onClick={() => setMenuOpen(false)}
              style={{
                background: "#1A1AE6",
                color: "white",
                padding: "8px 16px",
                borderRadius: "8px",
                textAlign: "center",
                fontWeight: "600",
                textDecoration: "none",
              }}
            >
              Obtenir un devis →
            </Link>
          )}
        </div>
      )}
    </nav>
  );
}

export default Navbar;